"use client";

import { useState, useMemo, useCallback } from "react";
import Box from "@/shared/ui/components/box";
import { CashbackDetailsModal } from "@/modules/plan-selection/components/cashback-details-modal";
import CashbackTag from "@/shared/ui/components/cashback-tag";
import { BuyerProtectionModal } from "@/modules/plan-selection/components/buyer-protection-modal";
import { Currency, LOCALIZED_ICON_TYPE, type AppContext } from "@/types";
import { useTranslation } from "react-i18next";
import MoneyAmount from "@/shared/ui/components/money-amount";
import * as events from "@/events";

// =============================================================================
// Types & Interfaces
// =============================================================================

export type PaymentOptionType = "PAY_NOW" | "PAY_NEXT_MONTH";

interface PaymentOptionCardProps {
  type: PaymentOptionType;
  onSelect: () => void;
  ctx: AppContext;
  disabled?: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const TENURE_MAP: Record<PaymentOptionType, number> = {
  PAY_NOW: 0,
  PAY_NEXT_MONTH: 1,
};

// =============================================================================
// Main Component
// =============================================================================

export function PaymentOptionCard({
  type,
  onSelect,
  ctx,
  disabled = false,
}: PaymentOptionCardProps) {
  const { t, i18n } = useTranslation();
  const [isCashbackDetailsOpen, setIsCashbackDetailsOpen] = useState(false);
  const [isBuyerProtectionOpen, setIsBuyerProtectionOpen] = useState(false);
  const [isBuyerProtectionClosing, setIsBuyerProtectionClosing] =
    useState(false);

  // =============================================================================
  // Event Handlers
  // =============================================================================

  const handleCloseCashbackDetails = useCallback(() => {
    setIsCashbackDetailsOpen(false);
  }, []);

  const handleCloseBuyerProtection = useCallback(() => {
    setIsBuyerProtectionClosing(true);
    setTimeout(() => {
      setIsBuyerProtectionClosing(false);
      setIsBuyerProtectionOpen(false);
    }, 300);
  }, []);

  const handleBuyerProtectionClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.stopPropagation();
      setIsBuyerProtectionOpen(true);
      ctx.sendEvent(events.BUYER_PROTECTION_LABEL_CLICKED, {
        payment_type: type,
      });
    },
    [disabled, type, ctx],
  );

  const handleBuyerProtectionModalClose = useCallback(() => {
    handleCloseBuyerProtection();
    ctx.sendEvent(events.CLOSE_MODAL_CLICKED, {
      modal_name: "buyer_protection",
      payment_type: type,
    });
  }, [type, ctx, handleCloseBuyerProtection]);

  // =============================================================================
  // Computed Values & Hooks
  // =============================================================================

  const tenure = TENURE_MAP[type];

  const optionData = useMemo(() => {
    const option = ctx.session.paymentOptions.find(
      (opt) => opt.tenure === tenure,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) as any;

    const cashbackAmt = option?.cashback?.actualAmount?.amount ?? 0;
    const cashbackPercent = option?.cashback?.percent ?? 0;
    const eligible = option?.cashback?.eligibleForCashback === true;
    const downPaymentAmount = option?.downPaymentAmount?.amount ?? 0;
    const totalToBeRepaidAmount = option?.totalToBeRepaidAmount?.amount ?? 0;
    const firstRepaymentDate = option?.firstRepaymentDate ?? null;

    return {
      cashbackAmt,
      cashbackPercent,
      eligible,
      downPaymentAmount,
      totalToBeRepaidAmount,
      firstRepaymentDate,
    };
  }, [ctx.session.paymentOptions, tenure]);

  const formattedPaymentDate = useMemo(() => {
    if (type !== "PAY_NEXT_MONTH") return null;
    if (!optionData.firstRepaymentDate) return null;

    const date = new Date(optionData.firstRepaymentDate);
    const day = date.getDate();
    const locale = i18n.language.startsWith("ar") ? "ar-SA" : "en-US";
    // Use Gregorian calendar so AR shows أبريل (April) not شوال (Hijri month).
    const localeGregorian = `${locale.replace("_", "-")}-u-ca-gregory`;

    const getOrdinalSuffix = (n: number) => {
      if (locale.startsWith("ar")) return "";
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };

    const monthName = new Intl.DateTimeFormat(localeGregorian, {
      month: "long",
    }).format(date);
    const year = date.getFullYear();

    if (locale.startsWith("ar")) {
      return t("plan.pay_on_date", { day, month: monthName, year });
    }

    return t("plan.pay_on_date", {
      day: `${day}${getOrdinalSuffix(day)}`,
      month: monthName,
      year,
    });
  }, [type, optionData, i18n.language, t]);

  const labels = useMemo(() => {
    if (type === "PAY_NOW") {
      return {
        prefix: t("plan.pay_today_prefix"),
        suffix: t("plan.pay_today_suffix"),
      };
    }
    return {
      prefix: t("plan.pay_next_month_prefix"),
      suffix: t("plan.pay_next_month_suffix"),
    };
  }, [type, t]);

  const displayAmount = useMemo(() => {
    return type === "PAY_NEXT_MONTH"
      ? optionData.totalToBeRepaidAmount
      : optionData.downPaymentAmount;
  }, [type, optionData.totalToBeRepaidAmount, optionData.downPaymentAmount]);

  const showBuyerProtection = useMemo(() => type === "PAY_NOW", [type]);

  const showPaymentDate = useMemo(
    () => type === "PAY_NEXT_MONTH" && !!formattedPaymentDate,
    [type, formattedPaymentDate],
  );

  // =============================================================================
  // Event Handlers (dependent on computed values)
  // =============================================================================

  const handleCashbackTagClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.stopPropagation();
      ctx.sendEvent(events.CASHBACK_TAG_CLICKED, {
        cashback_percent: optionData.cashbackPercent,
        payment_type: type,
      });
      setIsCashbackDetailsOpen(true);
    },
    [disabled, type, ctx, optionData.cashbackPercent],
  );

  // =============================================================================
  // Render
  // =============================================================================

  return (
    <>
      <Box
        radius="md"
        className={`relative transition-all px-6 py-4 ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
        onClick={disabled ? undefined : onSelect}
      >
        {optionData.cashbackAmt > 0 && (
          <div className="absolute top-[-12px] right-[24px]">
            <button
              onClick={handleCashbackTagClick}
              disabled={disabled}
              className={disabled ? "cursor-not-allowed" : "cursor-pointer"}
            >
              <CashbackTag amount={optionData.cashbackPercent} />
            </button>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <div className="text-[18px] font-semibold text-gray-900">
              <span className="mr-1 rtl:mr-0 rtl:ml-1">{labels.prefix}</span>
              <MoneyAmount
                amount={displayAmount}
                currency={ctx.session.order.currency}
                className="inline-flex mx-1"
                language={i18n.language}
              />
              <span className="ml-1 rtl:ml-0 rtl:mr-1">{labels.suffix}</span>
            </div>
            {showBuyerProtection && (
              <button
                onClick={handleBuyerProtectionClick}
                disabled={disabled}
                className={`text-[#585B7A] text-sm underline transition-colors ${disabled ? "cursor-not-allowed" : "hover:text-purple-600"}`}
              >
                {t("plan.full_buyer_protection")}
              </button>
            )}
            {showPaymentDate && (
              <div className="text-[#585B7A] text-sm mt-1">
                {formattedPaymentDate}
              </div>
            )}
          </div>
          {ctx.localizedIcon(LOCALIZED_ICON_TYPE.CHEVRON_FORWARD)}
        </div>
      </Box>

      <CashbackDetailsModal
        isOpen={isCashbackDetailsOpen}
        onClose={handleCloseCashbackDetails}
        cartValue={optionData.downPaymentAmount}
        cashbackAmount={optionData.cashbackAmt}
        ctx={ctx}
        currency={Currency[ctx.session.order.currency]}
        cashbackPercent={optionData.cashbackPercent}
      />

      <BuyerProtectionModal
        isOpen={isBuyerProtectionOpen}
        isClosing={isBuyerProtectionClosing}
        onClose={handleBuyerProtectionModalClose}
      />
    </>
  );
}
